import { IRetryPolicy, RetryContext } from "@microsoft/signalr";
import { AuthManager } from "./Auth";

type SignalRAuthManager = Pick<
  AuthManager,
  | "getAccessToken"
  | "isUserOverride"
  | "logoutAfterUnauthorized"
  | "recoverOidcSession"
  | "recoverUserOverride"
>;

type RetryDelayFactory = (retryContext: RetryContext) => number | null;

const defaultRetryDelays = [0, 2000, 10000, 30000];

export const defaultSignalRRetryDelay: RetryDelayFactory = (retryContext) => {
  return defaultRetryDelays[retryContext.previousRetryCount] ?? null;
};

export const isSignalRUnauthorizedError = (error?: Error) => {
  const errorWithStatus = error as
    | (Error & { status?: number; statusCode?: number })
    | undefined;

  return (
    errorWithStatus?.status === 401 ||
    errorWithStatus?.statusCode === 401 ||
    /status code[^0-9]*401/i.test(errorWithStatus?.message ?? "")
  );
};

export class SignalROidcRecovery {
  private lastAccessToken?: string;
  private recoveryAttempted = false;
  private recoveryPromise?: Promise<string>;
  private terminal = false;
  private logoutPromise?: Promise<void>;

  readonly retryPolicy: IRetryPolicy;

  constructor(
    private readonly authManager: SignalRAuthManager,
    private readonly fallbackRetryDelay: RetryDelayFactory = defaultSignalRRetryDelay
  ) {
    this.retryPolicy = {
      nextRetryDelayInMilliseconds: this.nextRetryDelayInMilliseconds,
    };
  }

  accessTokenFactory = async () => {
    try {
      const accessToken = this.recoveryPromise
        ? await this.recoveryPromise
        : await this.authManager.getAccessToken();
      this.lastAccessToken = accessToken;
      return accessToken;
    } catch {
      return "";
    }
  };

  start = async (startConnection: () => Promise<void>) => {
    try {
      await startConnection();
      this.markConnected();
    } catch (error) {
      if (!isSignalRUnauthorizedError(error as Error)) {
        throw error;
      }

      this.recoveryAttempted = true;
      await this.beginRecovery();

      try {
        await startConnection();
        this.markConnected();
      } catch (retryError) {
        if (isSignalRUnauthorizedError(retryError as Error)) {
          await this.logoutOnce();
        }

        throw retryError;
      }
    }
  };

  markConnected = () => {
    this.recoveryAttempted = false;
    this.recoveryPromise = undefined;
    this.terminal = false;
    this.logoutPromise = undefined;
  };

  private nextRetryDelayInMilliseconds = (retryContext: RetryContext) => {
    if (this.terminal) {
      return null;
    }

    if (!isSignalRUnauthorizedError(retryContext.retryReason)) {
      return this.fallbackRetryDelay(retryContext);
    }

    if (this.recoveryAttempted) {
      void this.logoutOnce();
      return null;
    }

    this.recoveryAttempted = true;
    void this.beginRecovery();
    return 0;
  };

  private beginRecovery = () => {
    if (!this.recoveryPromise) {
      // While impersonating, the connection must come back with a fresh
      // override token; recovering the master session would reconnect the hub
      // as the master while the user is still looking at the customer.
      const recovery = this.authManager.isUserOverride()
        ? this.authManager.recoverUserOverride(this.lastAccessToken)
        : this.authManager.recoverOidcSession(this.lastAccessToken);

      this.recoveryPromise = recovery
        .catch((error) => {
          this.terminal = true;
          throw error;
        });
      void this.recoveryPromise.catch(() => undefined);
    }

    return this.recoveryPromise;
  };

  private logoutOnce = () => {
    this.terminal = true;
    if (!this.logoutPromise) {
      this.logoutPromise = this.authManager.logoutAfterUnauthorized("signalr-401");
      void this.logoutPromise.catch(() => undefined);
    }

    return this.logoutPromise;
  };
}
